import React, { useContext } from 'react';
import { List, Button } from 'antd';
import Searcher from 'components/Molecules/Searcher/index';
import { Field, FieldArray } from 'formik';
import SupplierListItem from 'components/Molecules/ListItem/SupplierListItem';
import FactoryListItem from 'components/Molecules/ListItem/FactoryListItem';
import ProductListItem from 'components/Molecules/ListItem/ProductListItem';
import { ReactComponent as PlusCircleIcon } from 'assets/icons/New/FilterBar/PlusCircle.svg';
import T from 'i18n-react';
import classes from './SubForm.module.scss';
import { NS } from '../../constants';
import { QMASDocumentContext } from '../../context';

export default function SaveTo({ relatedSource }) {
  const { readonly } = useContext(QMASDocumentContext);
  const [searcher, setSearcher] = React.useState({
    type: '',
  });

  const getSelectedDiff = (previous, selected) => {
    const flatPrevious = previous.map((item) => item.id);
    const newSelected = selected
      .filter(({ id }) => !flatPrevious.includes(id))
      .map(({ deletable = true, ...item }) => ({ ...item, deletable }));
    return newSelected;
  };

  const getItemsToRemove = (previous, selected) => {
    const flatSelected = selected.map((item) => item.id);
    const willRemove = previous.filter(
      ({ id, deletable = true }) => !flatSelected.includes(id) && deletable
    );
    return willRemove;
  };

  const handleSelectFromSearcher = (field, selected) => {
    const newSelected = getSelectedDiff(field.value, selected);
    const removed = getItemsToRemove(field.value, selected);
    const flatRemoved = removed.map((item) => item.id);
    field.onChange({
      target: {
        name: field.name,
        value: field.value.filter(({ id }) => !flatRemoved.includes(id)).concat(newSelected),
      },
    });
  };

  const handleOpenSearcher = (type) => {
    setSearcher({ type });
  };

  const closeSearcher = () => {
    setSearcher({
      type: '',
    });
  };

  return (
    <React.Fragment>
      <Field name="saveTo.suppliers">
        {({ field }) => {
          return (
            <FieldArray name={field.name}>
              {({ remove }) => {
                return (
                  <React.Fragment>
                    <List
                      style={{ paddingTop: 8 }}
                      className={classes.list}
                      split={false}
                      dataSource={field.value || []}
                      rowKey={({ id }) => id}
                      renderItem={(item, index) => {
                        return (
                          <SupplierListItem
                            name={item.name}
                            supplierId={item.supplierId}
                            address={item.address || item.address1 || item.address2}
                            status={item.status}
                            hideContactPerson
                            hideContactPersonPhone
                            hideEmail
                            extra={
                              <Button
                                hidden={!item.deletable}
                                disabled={readonly}
                                type="primary"
                                danger
                                ghost
                                style={{ marginRight: 8 }}
                                onClick={() => {
                                  remove(index);
                                }}>
                                {T.translate('general.remove')}
                              </Button>
                            }
                          />
                        );
                      }}
                      header={
                        <Button
                          disabled={readonly}
                          className={classes.plusBtn}
                          icon={<PlusCircleIcon />}
                          type="link"
                          onClick={() => {
                            handleOpenSearcher('supplier');
                          }}>
                          {T.translate(`${NS}.addSupplier`)}
                        </Button>
                      }
                      locale={{
                        emptyText: ' ',
                      }}
                    />
                    <Searcher
                      title="Select Supplier"
                      type="supplier"
                      selected={field.value}
                      open={searcher.type === 'supplier'}
                      onCancel={closeSearcher}
                      onOk={(selected) => {
                        handleSelectFromSearcher(field, selected);
                        closeSearcher();
                      }}
                      otherSource={relatedSource.suppliers}
                    />
                  </React.Fragment>
                );
              }}
            </FieldArray>
          );
        }}
      </Field>
      <Field name="saveTo.factories">
        {({ field }) => {
          return (
            <FieldArray name={field.name}>
              {({ remove }) => {
                return (
                  <React.Fragment>
                    <List
                      className={classes.list}
                      split={false}
                      dataSource={field.value || []}
                      rowKey={({ id }) => id}
                      renderItem={(item, index) => {
                        return (
                          <FactoryListItem
                            certifications={[]}
                            name={item.name}
                            factoryId={item.factoryId}
                            address={item.address || item.address1 || item.address2}
                            extra={
                              <Button
                                hidden={!item.deletable}
                                disabled={readonly}
                                type="primary"
                                danger
                                ghost
                                style={{ marginRight: 8 }}
                                onClick={() => {
                                  remove(index);
                                }}>
                                {T.translate('general.remove')}
                              </Button>
                            }
                          />
                        );
                      }}
                      header={
                        <Button
                          disabled={readonly}
                          className={classes.plusBtn}
                          icon={<PlusCircleIcon />}
                          type="link"
                          onClick={() => {
                            handleOpenSearcher('factory');
                          }}>
                          {T.translate(`${NS}.addFactory`)}
                        </Button>
                      }
                      locale={{
                        emptyText: ' ',
                      }}
                    />
                    <Searcher
                      title="Select Factory"
                      type="factory"
                      selected={field.value}
                      open={searcher.type === 'factory'}
                      onCancel={closeSearcher}
                      onOk={(selected) => {
                        handleSelectFromSearcher(field, selected);
                        closeSearcher();
                      }}
                      otherSource={relatedSource.factories}
                    />
                  </React.Fragment>
                );
              }}
            </FieldArray>
          );
        }}
      </Field>
      <Field name="saveTo.products">
        {({ field }) => {
          return (
            <FieldArray name={field.name}>
              {({ remove }) => {
                return (
                  <React.Fragment>
                    <List
                      style={{ paddingBottom: 8 }}
                      className={classes.list}
                      split={false}
                      dataSource={field.value || []}
                      rowKey={({ id }) => id}
                      renderItem={(item, index) => {
                        return (
                          <ProductListItem
                            image={item.image}
                            name={item.name}
                            itemNo={item.itemNo}
                            extra={
                              <Button
                                hidden={!item.deletable}
                                disabled={readonly}
                                type="primary"
                                danger
                                ghost
                                style={{ marginRight: 8 }}
                                onClick={() => {
                                  remove(index);
                                }}>
                                {T.translate('general.remove')}
                              </Button>
                            }
                          />
                        );
                      }}
                      header={
                        <Button
                          disabled={readonly}
                          className={classes.plusBtn}
                          icon={<PlusCircleIcon />}
                          type="link"
                          onClick={() => {
                            handleOpenSearcher('product');
                          }}>
                          {T.translate(`${NS}.addProduct`)}
                        </Button>
                      }
                      locale={{
                        emptyText: ' ',
                      }}
                    />
                    <Searcher
                      title="Select Product"
                      type="product"
                      selected={field.value}
                      open={searcher.type === 'product'}
                      onCancel={closeSearcher}
                      onOk={(selected) => {
                        handleSelectFromSearcher(field, selected);
                        closeSearcher();
                      }}
                      otherSource={relatedSource.products}
                    />
                  </React.Fragment>
                );
              }}
            </FieldArray>
          );
        }}
      </Field>
    </React.Fragment>
  );
}
